import React, { useState, useMemo } from 'react';
import { 
  Zap, 
  Wallet, 
  BarChart2, 
  ChevronRight, 
  TrendingUp, 
  ShieldCheck, 
  ArrowUpRight
} from 'lucide-react';
import { PRICING_CONFIG } from '../config/pricingConfig';

const CompoundGrowthSimulator: React.FC = () => {
  const [activeTierIndex, setActiveTierIndex] = useState(0);
  const tier = PRICING_CONFIG.PRICING_TIERS[activeTierIndex];

  const simulation = useMemo(() => {
    const chuiCost = tier.subFee;
    const digitalCost = PRICING_CONFIG.calculateCompetitorCost(tier.totalLimit, 'DIGITAL_LENDERS');
    const bankCost = PRICING_CONFIG.calculateCompetitorCost(tier.totalLimit, 'BANKS');
    
    const initialMonthly = tier.minSales * 30;
    const projectedDaily = PRICING_CONFIG.projectGrowthBenefit(tier.minSales, 18);
    const projectedMonthly = projectedDaily * 30;
    
    return {
      savingsVsDigital: digitalCost - chuiCost,
      savingsVsBank: bankCost - chuiCost,
      initialMonthly,
      projectedMonthly,
      growthRevenue: projectedMonthly - initialMonthly,
      growthPercent: ((projectedMonthly - initialMonthly) / initialMonthly) * 100,
      chuiCost,
      digitalCost
    };
  }, [activeTierIndex]);

  return (
    <div className="bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200">
      <div className="grid lg:grid-cols-12">
        
        {/* Tier Selector Sidebar */}
        <div className="lg:col-span-4 bg-slate-100 border-r border-slate-200 p-8 lg:p-12">
          <div className="flex items-center space-x-4 mb-10">
            <div className="w-12 h-12 bg-chui-navy rounded-2xl flex items-center justify-center text-chui-yellow shadow-xl shadow-chui-navy/20">
              <BarChart2 size={24} />
            </div>
            <div>
              <h3 className="text-xl font-black text-chui-navy tracking-tight">Tier Selection</h3>
              <p className="text-[10px] text-slate-600 font-black uppercase tracking-[0.2em]">Match your scale</p>
            </div>
          </div>

          <div className="space-y-3">
            {PRICING_CONFIG.PRICING_TIERS.map((t, idx) => (
              <button 
                key={t.no}
                onClick={() => setActiveTierIndex(idx)}
                className={`w-full p-5 rounded-2xl flex items-center justify-between transition-all duration-300 group ${
                  activeTierIndex === idx 
                  ? 'bg-chui-navy text-white shadow-2xl translate-x-1' 
                  : 'bg-white text-slate-700 hover:border-chui-navy/50 border border-slate-200 hover:bg-white/80'
                }`}
              >
                <div className="flex flex-col items-start">
                  <span className={`text-[9px] font-black uppercase tracking-widest mb-1 ${activeTierIndex === idx ? 'text-chui-yellow' : 'text-slate-500'}`}>Tier {t.no}</span>
                  <span className="font-black text-sm tracking-tight">{t.name}</span>
                </div>
                <ChevronRight size={16} className={`${activeTierIndex === idx ? 'opacity-100 translate-x-1' : 'opacity-40'} transition-transform`} />
              </button>
            ))}
          </div>

          <div className="mt-12 p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
             <div className="flex items-center space-x-3 mb-3">
                <ShieldCheck size={16} className="text-chui-navy" />
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">Eligibility</span>
             </div>
             <p className="text-[11px] font-bold text-slate-600 leading-relaxed uppercase tracking-tight">
                Requires daily turnover of <span className="text-chui-navy font-black">KES {tier.minSales.toLocaleString()}+</span>
             </p>
          </div>
        </div>

        {/* Impact Numbers Area */}
        <div className="lg:col-span-8 p-10 lg:p-16 flex flex-col justify-between animate-in fade-in duration-500">
          
          <div className="grid md:grid-cols-2 gap-y-16 gap-x-12 mb-16">
            
            {/* Capital Tranche Logic */}
            <div className="space-y-8">
              <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.3em] flex items-center">
                <Wallet size={14} className="mr-3" /> Capital Configuration
              </h4>
              <div className="space-y-10">
                <div className="group">
                   <p className="text-sm font-black text-slate-600 uppercase tracking-widest mb-2 group-hover:text-chui-navy transition-colors">Individual Tranche</p>
                   <p className="text-5xl font-black text-chui-navy tracking-tighter">KES {tier.trancheLimit.toLocaleString()}</p>
                   <p className="text-[10px] font-bold text-slate-600 mt-2">Cycle: {tier.trancheDays} Days</p>
                </div>
                <div>
                   <p className="text-sm font-black text-slate-600 uppercase tracking-widest mb-2">Total 180-Day Limit</p>
                   <p className="text-4xl font-black text-slate-800 tracking-tighter">KES {tier.totalLimit.toLocaleString()}</p>
                   <p className="text-[10px] font-bold text-slate-600 mt-2">18 Repetition Tranches</p>
                </div>
              </div>
            </div>

            {/* Savings & Cost Arbitrage */}
            <div className="space-y-8">
              <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.3em] flex items-center">
                <Zap size={14} className="mr-3 text-chui-yellow" /> Cost Arbitrage
              </h4>
              <div className="space-y-10">
                <div className="p-8 bg-green-50 rounded-[2rem] border border-green-200 relative overflow-hidden group">
                   <div className="absolute top-0 right-0 p-4 opacity-10 rotate-12 group-hover:rotate-0 transition-transform">
                      <TrendingUp size={64} className="text-success" />
                   </div>
                   <p className="text-[10px] font-black text-success uppercase tracking-widest mb-3">Savings vs Digital Apps</p>
                   <p className="text-4xl font-black text-success tracking-tighter">KES {simulation.savingsVsDigital.toLocaleString()}</p>
                   <p className="text-[10px] font-bold text-success mt-3 italic uppercase tracking-tighter">94% Interest Reduction</p>
                </div>
                <div>
                   <p className="text-sm font-black text-slate-600 uppercase tracking-widest mb-2">Subscription Fee</p>
                   <p className="text-4xl font-black text-slate-800 tracking-tighter">KES {tier.subFee.toLocaleString()}</p>
                   <p className="text-[10px] font-bold text-slate-600 mt-2 uppercase tracking-widest">Fixed Pricing • No hidden fees</p>
                </div>
              </div>
            </div>

          </div>

          {/* Compound Growth Impact */}
          <div className="bg-slate-900 rounded-[2.5rem] p-10 text-white flex flex-col lg:flex-row items-center justify-between gap-10 relative overflow-hidden">
             <div className="absolute top-0 right-0 p-12 opacity-[0.03]">
                <TrendingUp size={200} />
             </div>
             
             <div className="relative z-10 space-y-4 text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start space-x-3 mb-2">
                   <div className="w-8 h-8 bg-chui-yellow rounded-lg flex items-center justify-center text-chui-navy">
                      <ArrowUpRight size={18} strokeWidth={3} />
                   </div>
                   <span className="text-xs font-black uppercase tracking-[0.3em] text-chui-yellow">Compounded Growth Impact</span>
                </div>
                <h5 className="text-2xl font-black tracking-tight max-w-sm leading-tight">
                  MSME Monthly Turnover <br />
                  Accelerates by <span className="text-chui-yellow">~{simulation.growthPercent.toFixed(1)}%</span>
                </h5>
             </div>

             <div className="relative z-10 flex flex-col md:flex-row items-center gap-12 bg-white/10 p-8 rounded-3xl border border-white/20">
                <div className="text-center md:text-left">
                   <p className="text-[9px] font-black text-white/50 uppercase tracking-[0.3em] mb-2">Organic Path</p>
                   <p className="text-lg font-black text-white/40 italic">KES {simulation.initialMonthly.toLocaleString()}</p>
                </div>
                <div className="w-px h-10 bg-white/20 hidden md:block" />
                <div className="text-center md:text-right">
                   <p className="text-[9px] font-black text-chui-yellow uppercase tracking-[0.3em] mb-2">Chui Powered Path</p>
                   <p className="text-2xl font-black text-white">KES {simulation.projectedMonthly.toLocaleString()}</p>
                </div>
             </div>
          </div>

          <div className="mt-8 flex justify-center text-[9px] font-black text-slate-500 uppercase tracking-[0.4em]">
             <span>Pilot Projections Only</span>
             <span className="mx-6">•</span>
             <span>Based on 18 Tranches</span>
             <span className="mx-6">•</span>
             <span>Character-Verified Results</span>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CompoundGrowthSimulator;